import { useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useStaleData } from "@/hooks/useStaleData";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronDown, ChevronUp, Info } from "lucide-react";
import PageLoader from "@/components/PageLoader";

const INCOME_SOURCES = ["salary", "freelance", "rental", "business", "dividend", "interest", "other"];
const TODAY = new Date().toISOString().slice(0, 10);

const emptyExpense = () => ({ id: Date.now() + Math.random(), date: TODAY, category_id: "", amount: "", description: "" });
const emptyIncome  = () => ({ id: Date.now() + Math.random(), date: TODAY, source_type: "salary", source: "", amount: "", description: "" });

export default function HistoricalEntry() {
  // ── Categories ──────────────────────────────────────────────────────────────
  const fetchCats = useCallback(async () => {
    const res = await axios.get(`${API}/categories`);
    return (res.data || []).filter((c) => c.type === "expense");
  }, []);
  const { data: expenseCats, loading: catsLoading } = useStaleData("bm_hist_cats", fetchCats, []);

  // ── Row state ────────────────────────────────────────────────────────────────
  const [expenseRows, setExpenseRows] = useState([emptyExpense()]);
  const [incomeRows,  setIncomeRows]  = useState([emptyIncome()]);
  const [saving,      setSaving]      = useState(false);
  const [expOpen,     setExpOpen]     = useState(true);
  const [incOpen,     setIncOpen]     = useState(true);

  // ── Row helpers ──────────────────────────────────────────────────────────────
  const updateExpRow = (id, field, val) =>
    setExpenseRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const updateIncRow = (id, field, val) =>
    setIncomeRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));

  const removeExpRow = (id) => setExpenseRows((r) => r.filter((x) => x.id !== id));
  const removeIncRow = (id) => setIncomeRows((r)  => r.filter((x) => x.id !== id));

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const validExp = expenseRows.filter((r) => r.category_id && parseFloat(r.amount) > 0);
    const validInc = incomeRows.filter((r)  => parseFloat(r.amount) > 0);

    if (!validExp.length && !validInc.length) {
      toast.error("Add at least one expense or income entry before saving.");
      return;
    }

    setSaving(true);
    try {
      let totalInserted = 0;
      let totalErrors   = 0;

      if (validExp.length) {
        const res = await axios.post(`${API}/transactions/bulk`, {
          transactions: validExp.map((r) => ({
            date:        r.date,
            category_id: r.category_id,
            amount:      parseFloat(r.amount),
            description: r.description,
          })),
        });
        totalInserted += res.data.inserted || 0;
        totalErrors   += res.data.skipped  || 0;
        if (res.data.errors?.length) {
          res.data.errors.forEach((e) => toast.error(`Row ${e.index + 1}: ${e.error}`));
        }
      }

      if (validInc.length) {
        const res = await axios.post(`${API}/income-entries/bulk`, {
          entries: validInc.map((r) => ({
            date:        r.date,
            amount:      parseFloat(r.amount),
            source_type: r.source_type,
            source:      r.source,
            description: r.description,
          })),
        });
        totalInserted += res.data.inserted || 0;
        totalErrors   += res.data.skipped  || 0;
      }

      if (totalInserted > 0) {
        toast.success(`Saved ${totalInserted} entr${totalInserted === 1 ? "y" : "ies"} successfully.`);
        // Reset to fresh rows after successful save
        setExpenseRows([emptyExpense()]);
        setIncomeRows([emptyIncome()]);
      }
      if (totalErrors > 0) {
        toast.error(`${totalErrors} entr${totalErrors === 1 ? "y" : "ies"} could not be saved — check the errors above.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (catsLoading) return (
    <div className="min-h-[calc(100vh-80px)] bm-page-bg-stone flex items-center justify-center">
      <PageLoader message="Loading categories…" tips={["Preparing your entry form…"]} />
    </div>
  );

  return (
    <>
      <div className="min-h-[calc(100vh-80px)] bm-page-bg-stone px-4 py-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Historical Data Entry</h1>
            <p className="text-sm text-stone-500 mt-1">
              Enter past income and expenses — pick any previous date. These won't affect your current budget cycle.
            </p>
          </div>

          {/* Info banner */}
          <div className="flex items-start gap-3 bg-orange-50 border border-orange-100 rounded-2xl px-4 py-3">
            <Info size={16} className="text-orange-500 mt-0.5 shrink-0" />
            <p className="text-xs text-orange-700 leading-relaxed">
              Entries here are stored with their exact dates. Your <strong>current month&apos;s</strong> budget bars
              only count transactions within your current salary cycle — past data shows in reports and transaction history.
            </p>
          </div>

          {/* ── Income section ─────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setIncOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-stone-900">Income</span>
                <span className="text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-0.5 rounded-full">
                  {incomeRows.length} row{incomeRows.length !== 1 ? "s" : ""}
                </span>
              </div>
              {incOpen ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
            </button>

            {incOpen && (
              <div className="px-5 pb-5 space-y-3">
                {/* Column headers */}
                <div className="grid grid-cols-[130px_110px_1fr_100px_36px] gap-2 text-xs font-semibold text-stone-400 uppercase tracking-wide">
                  <span>Date</span>
                  <span>Type</span>
                  <span>Source / Note</span>
                  <span>Amount (₹)</span>
                  <span />
                </div>

                {incomeRows.map((row) => (
                  <div key={row.id} className="grid grid-cols-[130px_110px_1fr_100px_36px] gap-2 items-center">
                    <DatePicker
                      value={row.date}
                      max={TODAY}
                      onChange={(v) => updateIncRow(row.id, "date", v)}
                    />
                    <select
                      value={row.source_type}
                      onChange={(e) => updateIncRow(row.id, "source_type", e.target.value)}
                      className="h-9 px-2 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 focus:outline-none focus:border-orange-400"
                    >
                      {INCOME_SOURCES.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="e.g. Infosys salary"
                      value={row.source}
                      onChange={(e) => updateIncRow(row.id, "source", e.target.value)}
                      className="h-9 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 placeholder:text-stone-300 focus:outline-none focus:border-orange-400"
                    />
                    <input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={row.amount}
                      onChange={(e) => updateIncRow(row.id, "amount", e.target.value)}
                      className="h-9 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 placeholder:text-stone-300 focus:outline-none focus:border-orange-400"
                    />
                    <button
                      onClick={() => removeIncRow(row.id)}
                      disabled={incomeRows.length === 1}
                      className="h-9 w-9 flex items-center justify-center rounded-xl text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}

                <button
                  onClick={() => setIncomeRows((r) => [...r, emptyIncome()])}
                  className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 font-medium mt-1"
                >
                  <Plus size={15} /> Add row
                </button>
              </div>
            )}
          </div>

          {/* ── Expenses section ───────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setExpOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-stone-900">Expenses</span>
                <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">
                  {expenseRows.length} row{expenseRows.length !== 1 ? "s" : ""}
                </span>
              </div>
              {expOpen ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
            </button>

            {expOpen && (
              <div className="px-5 pb-5 space-y-3">
                {expenseCats.length === 0 ? (
                  <p className="text-sm text-stone-400 py-2">
                    No expense categories found. Go to <strong>Budget</strong> to create categories first.
                  </p>
                ) : (
                  <>
                    {/* Column headers */}
                    <div className="grid grid-cols-[130px_160px_1fr_100px_36px] gap-2 text-xs font-semibold text-stone-400 uppercase tracking-wide">
                      <span>Date</span>
                      <span>Category</span>
                      <span>Description</span>
                      <span>Amount (₹)</span>
                      <span />
                    </div>

                    {expenseRows.map((row) => (
                      <div key={row.id} className="grid grid-cols-[130px_160px_1fr_100px_36px] gap-2 items-center">
                        <DatePicker
                          value={row.date}
                          max={TODAY}
                          onChange={(v) => updateExpRow(row.id, "date", v)}
                        />
                        <select
                          value={row.category_id}
                          onChange={(e) => updateExpRow(row.id, "category_id", e.target.value)}
                          className="h-9 px-2 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 focus:outline-none focus:border-orange-400"
                        >
                          <option value="">Select category</option>
                          {expenseCats.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="e.g. DMart groceries"
                          value={row.description}
                          onChange={(e) => updateExpRow(row.id, "description", e.target.value)}
                          className="h-9 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 placeholder:text-stone-300 focus:outline-none focus:border-orange-400"
                        />
                        <input
                          type="number"
                          placeholder="0"
                          min="0"
                          value={row.amount}
                          onChange={(e) => updateExpRow(row.id, "amount", e.target.value)}
                          className="h-9 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-900 placeholder:text-stone-300 focus:outline-none focus:border-orange-400"
                        />
                        <button
                          onClick={() => removeExpRow(row.id)}
                          disabled={expenseRows.length === 1}
                          className="h-9 w-9 flex items-center justify-center rounded-xl text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={() => setExpenseRows((r) => [...r, emptyExpense()])}
                      className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 font-medium mt-1"
                    >
                      <Plus size={15} /> Add row
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Save button ─────────────────────────────────────────────────── */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold text-sm rounded-2xl py-3.5 transition-colors shadow-sm"
          >
            <Save size={16} />
            {saving ? "Saving…" : "Save All Entries"}
          </button>

        </div>
      </div>
    </>
  );
}
